import { Form, Input, Button, Card, message, Tabs, Checkbox, Space } from 'antd';
import { LockOutlined, UserOutlined, MobileOutlined, MailOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { platformApi } from '../../api/platform';
import { usePlatformAuth } from '../../stores/auth.platform';
import { CaptchaInput, CaptchaInputHandle } from '../../components/CaptchaInput';
import { firstAllowedPath } from '../../constants/permissions';

type Method = 'password' | 'sms' | 'email';
const REMEMBER_KEY = 'platform_login_remember';

interface RememberedAccount {
  method: Method;
  username?: string;
  phone?: string;
  email?: string;
}

function loadRemembered(): RememberedAccount | null {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function PlatformLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = usePlatformAuth(s => s.setAuth);
  const [method, setMethod] = useState<Method>('password');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [pwForm] = Form.useForm();
  const [smsForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const smsCaptchaRef = useRef<CaptchaInputHandle>(null);
  const emailCaptchaRef = useRef<CaptchaInputHandle>(null);

  // 启动时回填上次「记住」的标识
  useEffect(() => {
    const r = loadRemembered();
    if (!r) return;
    setMethod(r.method);
    if (r.username) pwForm.setFieldsValue({ username: r.username, remember: true });
    if (r.phone) smsForm.setFieldsValue({ phone: r.phone, remember: true });
    if (r.email) emailForm.setFieldsValue({ email: r.email, remember: true });
  }, [pwForm, smsForm, emailForm]);

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const afterLogin = (jwt: string, user: any, permissions: string[], remembered: RememberedAccount | null) => {
    setAuth(jwt, user, permissions);
    if (remembered) localStorage.setItem(REMEMBER_KEY, JSON.stringify(remembered));
    else localStorage.removeItem(REMEMBER_KEY);
    const redirect = (location.state as any)?.from?.pathname || firstAllowedPath(permissions) || '/dashboard';
    navigate(redirect, { replace: true });
  };

  const handleAuthError = (e: any) => {
    const code = e.response?.data?.error_code;
    const map: Record<string, string> = {
      invalid_credentials: '账号或密码错误',
      'invalid credentials': '账号或密码错误',
      invalid_phone: '手机号格式错误',
      invalid_email: '邮箱格式错误',
      invalid_code: '验证码错误或已过期',
    };
    message.error(map[code] || '登录失败');
  };

  // ─── 账号密码 ───
  const onPasswordLogin = async (v: { username: string; password: string; remember?: boolean }) => {
    setLoading(true);
    try {
      const { jwt, user, permissions } = await platformApi.login(v.username, v.password);
      afterLogin(jwt, user, permissions, v.remember ? { method: 'password', username: v.username } : null);
    } catch (e: any) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  };

  // ─── 短信验证码 ───
  const onSendSmsCode = async () => {
    const captcha = smsForm.getFieldValue('captcha') as string | undefined;
    if (!captcha || !smsCaptchaRef.current?.validate(captcha)) {
      message.error('请输入正确的图形验证码');
      smsCaptchaRef.current?.refresh();
      smsForm.setFieldValue('captcha', '');
      return;
    }
    try {
      await smsForm.validateFields(['phone']);
    } catch { return; }
    try {
      await platformApi.requestSmsCode(smsForm.getFieldValue('phone'));
      message.success('验证码已发送');
      setCountdown(60);
    } catch (e: any) {
      handleAuthError(e);
    }
  };
  const onSmsLogin = async (v: { phone: string; code: string; remember?: boolean }) => {
    setLoading(true);
    try {
      const { jwt, user, permissions } = await platformApi.loginBySms(v.phone, v.code);
      afterLogin(jwt, user, permissions, v.remember ? { method: 'sms', phone: v.phone } : null);
    } catch (e: any) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  };

  // ─── 邮箱验证码 ───
  const onSendEmailCode = async () => {
    const captcha = emailForm.getFieldValue('captcha') as string | undefined;
    if (!captcha || !emailCaptchaRef.current?.validate(captcha)) {
      message.error('请输入正确的图形验证码');
      emailCaptchaRef.current?.refresh();
      emailForm.setFieldValue('captcha', '');
      return;
    }
    try {
      await emailForm.validateFields(['email']);
    } catch { return; }
    try {
      await platformApi.requestEmailCode(emailForm.getFieldValue('email'));
      message.success('验证码已发送');
      setCountdown(60);
    } catch (e: any) {
      handleAuthError(e);
    }
  };
  const onEmailLogin = async (v: { email: string; code: string; remember?: boolean }) => {
    setLoading(true);
    try {
      const { jwt, user, permissions } = await platformApi.loginByEmail(v.email, v.code);
      afterLogin(jwt, user, permissions, v.remember ? { method: 'email', email: v.email } : null);
    } catch (e: any) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f0f2f5' }}>
      <Card style={{ width: 420 }} title="元睿 AI · 总部后台">
        {/*
          多方式登录功能开关。当前后端仅实现「账号密码」，短信/邮箱待 P2 实施：
          - GET  /api/admin/auth/captcha
          - POST /api/admin/auth/sms-code  + /email-code
          - POST /api/admin/auth/login-sms + /login-email
          后端就绪后把下方常量改成 true 即可恢复 3 个 tab。表单逻辑、CaptchaInput、
          smsForm/emailForm hooks 全部保留不动（dead code 由 tree-shake 自动剔除）。
        */}
        {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
        {(() => {
          const FEATURE_MULTI_METHOD_LOGIN = false;
          const items = [
            {
              key: 'password',
              label: '账号密码',
              children: (
                <Form form={pwForm} layout="vertical" onFinish={onPasswordLogin}>
                  <Form.Item name="username" rules={[{ required: true, min: 3, message: '至少 3 字符' }]}>
                    <Input prefix={<UserOutlined />} placeholder="用户名" autoFocus />
                  </Form.Item>
                  <Form.Item name="password" rules={[{ required: true, min: 6, message: '至少 6 字符' }]}>
                    <Input.Password prefix={<LockOutlined />} placeholder="密码" />
                  </Form.Item>
                  <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>记住账号</Checkbox>
                  </Form.Item>
                  <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
                </Form>
              ),
            },
            ...(FEATURE_MULTI_METHOD_LOGIN ? [
              {
                key: 'sms',
                label: '短信验证码',
                children: (
                  <Form form={smsForm} layout="vertical" onFinish={onSmsLogin}>
                    <Form.Item name="phone" rules={[{ required: true, pattern: /^1\d{10}$/, message: '请输入 11 位手机号' }]}>
                      <Input prefix={<MobileOutlined />} placeholder="手机号" maxLength={11} />
                    </Form.Item>
                    <Form.Item name="captcha" rules={[{ required: true, len: 4, message: '4 位图形验证码' }]}>
                      <CaptchaInput ref={smsCaptchaRef} />
                    </Form.Item>
                    <Form.Item name="code" rules={[{ required: true, pattern: /^\d{6}$/, message: '6 位短信验证码' }]}>
                      <Space.Compact style={{ width: '100%' }}>
                        <Input placeholder="短信验证码" maxLength={6} style={{ flex: 1 }} />
                        <Button onClick={onSendSmsCode} disabled={countdown > 0}>
                          {countdown > 0 ? `${countdown}s` : '发送验证码'}
                        </Button>
                      </Space.Compact>
                    </Form.Item>
                    <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 12 }}>
                      <Checkbox>记住手机号</Checkbox>
                    </Form.Item>
                    <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
                  </Form>
                ),
              },
              {
                key: 'email',
                label: '邮箱验证码',
                children: (
                  <Form form={emailForm} layout="vertical" onFinish={onEmailLogin}>
                    <Form.Item name="email" rules={[{ required: true, type: 'email', message: '请输入正确邮箱' }]}>
                      <Input prefix={<MailOutlined />} placeholder="邮箱" />
                    </Form.Item>
                    <Form.Item name="captcha" rules={[{ required: true, len: 4, message: '4 位图形验证码' }]}>
                      <CaptchaInput ref={emailCaptchaRef} />
                    </Form.Item>
                    <Form.Item name="code" rules={[{ required: true, pattern: /^\d{6}$/, message: '6 位邮箱验证码' }]}>
                      <Space.Compact style={{ width: '100%' }}>
                        <Input placeholder="邮箱验证码" maxLength={6} style={{ flex: 1 }} />
                        <Button onClick={onSendEmailCode} disabled={countdown > 0}>
                          {countdown > 0 ? `${countdown}s` : '发送验证码'}
                        </Button>
                      </Space.Compact>
                    </Form.Item>
                    <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 12 }}>
                      <Checkbox>记住邮箱</Checkbox>
                    </Form.Item>
                    <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
                  </Form>
                ),
              },
            ] : []),
          ];
          return (
            <Tabs
              activeKey={method}
              onChange={(k: string) => setMethod(k as Method)}
              items={items}
              // 只有 1 个 tab 时隐藏 tab 头，避免视觉浪费
              renderTabBar={items.length === 1 ? () => <></> : undefined}
            />
          );
        })()}
      </Card>
    </div>
  );
}
